import React from 'react';
import PropTypes from 'prop-types';

import { ELEVATED_PROMO_PRICE_ROW_LAYOUT } from '../../constants/campaignPromoVariants';
import { ELEVATED_CAMPAIGN_PROMO_UI } from '../../constants/elevatedCampaignPromoUi';

const ELEVATED_PROMO_PRICE_ROW_LAYOUTS = Object.values(
  ELEVATED_PROMO_PRICE_ROW_LAYOUT,
);

/** Invisible top line so Standard $0 aligns with Elevated promo row (Figma dev note). */
export const CampaignPriceAlignmentSpacer = () => {
  const { promoPrices } = ELEVATED_CAMPAIGN_PROMO_UI;
  return (
    <span
      className="pointer-events-none select-none text-2xl font-semibold leading-[30px] text-gray-90 opacity-0"
      aria-hidden
    >
      {promoPrices.monthly}
    </span>
  );
};

/**
 * One plan column: promo on top, struck-through list price + cadence below (Figma comparison table).
 */
export const PromoPriceColumn = ({
  listPrice,
  promoPrice,
  cadenceSuffix,
  billingNote,
  className = '',
  compact = false,
}) => (
  <div
    className={`flex flex-col items-center font-hg-sans ${
      compact ? 'py-0' : 'pt-3 pb-5'
    } ${className}`}
  >
    <p
      className={
        compact
          ? 'text-base font-semibold leading-6 text-gray-90'
          : 'text-2xl font-semibold leading-[30px] text-gray-90'
      }
    >
      {promoPrice}
    </p>
    <div className="flex items-center gap-1 whitespace-nowrap">
      <span
        className={
          compact
            ? 'text-sm font-semibold leading-5 text-gray-90 line-through decoration-coral-50 decoration-2'
            : 'text-xl font-semibold leading-8 text-gray-90 line-through decoration-coral-50 decoration-2'
        }
      >
        {listPrice}
      </span>
      <span
        className={
          compact
            ? 'text-xs font-extralight leading-4 text-gray-90'
            : 'text-sm font-extralight leading-5 text-gray-90'
        }
      >
        {cadenceSuffix}
      </span>
    </div>
    <span
      className={
        compact
          ? 'text-center text-xs font-extralight leading-4 text-gray-90'
          : 'text-center text-sm font-extralight leading-5 text-gray-90'
      }
    >
      {billingNote}
    </span>
  </div>
);

PromoPriceColumn.propTypes = {
  listPrice: PropTypes.string.isRequired,
  promoPrice: PropTypes.string.isRequired,
  cadenceSuffix: PropTypes.string.isRequired,
  billingNote: PropTypes.string.isRequired,
  className: PropTypes.string,
  compact: PropTypes.bool,
};

/** Hero / pricing blurb — desktop single line (7756:12882); mobile stacked lines (7756:12886). */
export const ElevatedPromoPriceInline = ({
  className = '',
  providersNote = '',
}) => {
  const { listPrices, promoPrices } = ELEVATED_CAMPAIGN_PROMO_UI;

  return (
    <div
      className={`font-hg-sans text-gray-90 ${className}`}
      data-qa-target="elevated-promo-price-inline"
    >
      <div className="flex flex-col gap-2 text-center md:hidden">
        <p className="text-xl font-semibold leading-[25px]">
          Elevated plans are currently
        </p>
        <p className="text-xl leading-[25px]">
          <span className="text-sm font-extralight leading-[21px] line-through decoration-coral-50 decoration-2">
            {`  ${listPrices.monthly}/mo`}
          </span>
          <span className="font-semibold">{` ${promoPrices.monthly} `}</span>
          <span className="font-semibold">monthly</span>
        </p>
        <p className="text-xl leading-[25px]">
          <span className="font-semibold">or{'  '}</span>
          <span className="text-sm font-extralight leading-[21px] line-through decoration-coral-50 decoration-2">
            {listPrices.annual}/yr
          </span>
          <span className="font-semibold">
            {` ${promoPrices.annual} annually`}
          </span>
        </p>
        {providersNote && (
          <p className="text-sm font-extralight leading-5 text-gray-90">
            {providersNote}
          </p>
        )}
      </div>
      <p className="hidden text-center md:block">
        <span className="text-2xl font-semibold leading-[30px]">
          Elevated plans are currently{' '}
        </span>
        <span className="text-base font-extralight leading-6 text-gray-90 line-through decoration-coral-50 decoration-2">
          {listPrices.monthly}/mo{' '}
        </span>
        <span className="text-2xl font-semibold leading-[30px]">
          {promoPrices.monthly}{' '}
        </span>
        <span className="text-2xl font-semibold leading-[30px]">monthly</span>
        <span className="text-2xl font-semibold leading-[30px]"> or </span>
        <span className="text-base font-extralight leading-6 text-gray-90 line-through decoration-coral-50 decoration-2">
          {listPrices.annual}/yr{' '}
        </span>
        <span className="text-2xl font-semibold leading-[30px]">
          {promoPrices.annual} annually.
        </span>
      </p>
      {providersNote && (
        <p className="mt-2 hidden text-center text-sm font-extralight leading-5 text-gray-90 md:block">
          {providersNote}
        </p>
      )}
    </div>
  );
};

ElevatedPromoPriceInline.propTypes = {
  className: PropTypes.string,
  providersNote: PropTypes.string,
};

export const ElevatedPromoPriceRow = ({
  layout = ELEVATED_PROMO_PRICE_ROW_LAYOUT.SPLIT,
  providersNote = '',
  stacked = false,
}) => {
  const { listPrices, promoPrices } = ELEVATED_CAMPAIGN_PROMO_UI;

  if (layout === ELEVATED_PROMO_PRICE_ROW_LAYOUT.INLINE) {
    return <ElevatedPromoPriceInline providersNote={providersNote} />;
  }

  if (stacked) {
    /** Figma mobile comparison (7745:27473): promo + cadence word, then struck list + /mo|/yr. */
    const MobileStackedPromoPriceColumn = ({
      listPrice,
      listSuffix,
      promoCadence,
      promoPrice,
    }) => (
      <div className="flex flex-col items-center gap-0">
        <p className="whitespace-nowrap text-center">
          <span className="text-xl font-semibold leading-[25px] text-gray-90">
            {promoPrice}{' '}
          </span>
          <span className="text-sm font-extralight leading-[21px] text-gray-90">
            {promoCadence}
          </span>
        </p>
        <div className="flex items-center gap-1 whitespace-nowrap">
          <span className="text-base font-semibold leading-6 text-gray-90 line-through decoration-coral-50 decoration-2">
            {listPrice}
          </span>
          <span className="text-sm font-extralight leading-5 text-gray-90">
            {listSuffix}
          </span>
        </div>
      </div>
    );

    return (
      <div
        className="flex w-full max-w-full flex-col items-center gap-2 py-1"
        data-qa-target="elevated-promo-price-stacked"
      >
        <MobileStackedPromoPriceColumn
          listPrice={listPrices.monthly}
          listSuffix="/mo"
          promoCadence="monthly"
          promoPrice={promoPrices.monthly}
        />
        <div className="h-px w-10 shrink-0 bg-gray-20" />
        <MobileStackedPromoPriceColumn
          listPrice={listPrices.annual}
          listSuffix="/yr"
          promoCadence="annually"
          promoPrice={promoPrices.annual}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <PromoPriceColumn
        className="w-[131px] shrink-0"
        listPrice={listPrices.monthly}
        promoPrice={promoPrices.monthly}
        cadenceSuffix="/ month"
        billingNote="billed monthly"
      />
      <div className="h-[60px] w-px shrink-0 bg-gray-20" />
      <PromoPriceColumn
        className="min-w-[124px] shrink-0"
        listPrice={listPrices.annual}
        promoPrice={promoPrices.annual}
        cadenceSuffix="/ year"
        billingNote="billed yearly"
      />
    </div>
  );
};

ElevatedPromoPriceRow.propTypes = {
  layout: PropTypes.oneOf(ELEVATED_PROMO_PRICE_ROW_LAYOUTS),
  providersNote: PropTypes.string,
  stacked: PropTypes.bool,
};

export default ElevatedPromoPriceRow;
